import React from "react";
import "./graphs.css";

export interface PlanterYieldEntry {
    attr_name: string;
    display_name: string;
    crop: string;
    yield_unit: string;
    correlation: number;
    corr_strength: string;
    n_fields: number;
    points: number[][];
}

interface PlanterYieldChartProps {
    data: PlanterYieldEntry[];
}

function ScatterPlot({ entry }: { entry: PlanterYieldEntry }) {
    const points = entry.points || [];
    if (points.length === 0) return null;

    const xValues = points.map((p) => p[0]);
    const yValues = points.map((p) => p[1]);

    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    const xRange = xMax - xMin || 1;
    const yRange = yMax - yMin || 1;

    const width = 280;
    const height = 220;
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const plotW = width - padding.left - padding.right;
    const plotH = height - padding.top - padding.bottom;

    const toX = (v: number) => padding.left + ((v - xMin) / xRange) * plotW;
    const toY = (v: number) => padding.top + plotH - ((v - yMin) / yRange) * plotH;

    // Linear regression for trend line
    const n = points.length;
    const sumX = xValues.reduce((a, b) => a + b, 0);
    const sumY = yValues.reduce((a, b) => a + b, 0);
    const sumXY = points.reduce((a, p) => a + p[0] * p[1], 0);
    const sumX2 = xValues.reduce((a, x) => a + x * x, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    const trendY1 = slope * xMin + intercept;
    const trendY2 = slope * xMax + intercept;

    // Format axis labels
    const fmtAxis = (v: number) => {
        if (Math.abs(v) >= 10000) return Math.round(v).toLocaleString();
        if (Math.abs(v) >= 100) return Math.round(v).toString();
        if (Math.abs(v) >= 1) return v.toFixed(1);
        return v.toFixed(2);
    };

    return (
        <div className="planterScatterContainer">
            <div className="planterScatterTitle">
                <span className="planterScatterName">{entry.display_name || entry.attr_name}</span>
                <br />
                <span className="planterScatterSubtitle">
                    r = {entry.correlation?.toFixed(3) ?? "N/A"} ({entry.corr_strength}) |{" "}
                    {entry.n_fields} field(s)
                </span>
            </div>
            <svg width={width} height={height} className="planterScatterSvg">
                {/* Y axis */}
                <line
                    x1={padding.left}
                    y1={padding.top}
                    x2={padding.left}
                    y2={padding.top + plotH}
                    stroke="#666"
                    strokeWidth={1}
                />
                {/* X axis */}
                <line
                    x1={padding.left}
                    y1={padding.top + plotH}
                    x2={padding.left + plotW}
                    y2={padding.top + plotH}
                    stroke="#666"
                    strokeWidth={1}
                />
                {/* Points */}
                {points.map((p, i) => (
                    <circle
                        key={i}
                        cx={toX(p[0])}
                        cy={toY(p[1])}
                        r={2}
                        fill="#4285f4"
                        opacity={0.6}
                    />
                ))}
                {/* Trend line */}
                <line
                    x1={toX(xMin)}
                    y1={toY(trendY1)}
                    x2={toX(xMax)}
                    y2={toY(trendY2)}
                    stroke="#e65100"
                    strokeWidth={2}
                />
                {/* Y axis labels */}
                <text
                    x={padding.left - 5}
                    y={padding.top + 4}
                    textAnchor="end"
                    fontSize={9}
                    fill="#999"
                >
                    {fmtAxis(yMax)}
                </text>
                <text
                    x={padding.left - 5}
                    y={padding.top + plotH + 4}
                    textAnchor="end"
                    fontSize={9}
                    fill="#999"
                >
                    {fmtAxis(yMin)}
                </text>
                {/* X axis labels */}
                <text
                    x={padding.left}
                    y={padding.top + plotH + 15}
                    textAnchor="start"
                    fontSize={9}
                    fill="#999"
                >
                    {fmtAxis(xMin)}
                </text>
                <text
                    x={padding.left + plotW}
                    y={padding.top + plotH + 15}
                    textAnchor="end"
                    fontSize={9}
                    fill="#999"
                >
                    {fmtAxis(xMax)}
                </text>
                {/* Axis titles */}
                <text
                    x={padding.left + plotW / 2}
                    y={height - 5}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#ccc"
                >
                    {entry.display_name || entry.attr_name}
                </text>
                <text
                    x={12}
                    y={padding.top + plotH / 2}
                    textAnchor="middle"
                    fontSize={10}
                    fill="#ccc"
                    transform={`rotate(-90, 12, ${padding.top + plotH / 2})`}
                >
                    Yield ({entry.yield_unit})
                </text>
            </svg>
        </div>
    );
}

export default function PlanterYieldChart({ data }: PlanterYieldChartProps) {
    if (!data || data.length === 0) return null;

    // Group charts by crop, preserving first-appearance order of crops and the
    // backend ordering within each crop. Metrics are never blended across crops.
    const cropOrder: string[] = [];
    const byCrop: Record<string, PlanterYieldEntry[]> = {};
    for (const entry of data) {
        const crop = entry.crop || "";
        if (!byCrop[crop]) {
            cropOrder.push(crop);
            byCrop[crop] = [];
        }
        byCrop[crop].push(entry);
    }

    return (
        <div className="planterYieldSection">
            <h3 className="planterYieldTitle">Planter Metric vs Yield</h3>
            <p className="planterYieldDescription">
                Each point is a grid cell (all fields of the crop combined) — the planting metric
                (x-axis) against yield (y-axis). The orange line shows the overall trend; an upward
                slope means the metric rose with yield, downward means it fell.
            </p>
            {cropOrder.map((crop) => (
                <div className="planterYieldCropGroup" key={crop || "unknown-crop"}>
                    {crop && <h4 className="planterYieldCropTitle">{crop}</h4>}
                    <div className="planterYieldGrid">
                        {byCrop[crop].map((entry) => (
                            <ScatterPlot key={entry.attr_name} entry={entry} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
